import { Link, NavLink, Outlet } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  GITHUB_OSS,
  MARKETING_ORIGIN,
  RAILWAY_DEPLOY,
} from "@/lib/hosts";

type NavItem = { to: string; label: string; group?: string };

const NAV: NavItem[] = [
  { to: "/", label: "Overview" },
  { to: "/oss", label: "Self-host (OSS)", group: "Get started" },
  { to: "/railway", label: "Railway 1-click" },
  { to: "/agents/connect", label: "Connect an agent", group: "Agents" },
  { to: "/agents/radio", label: "Radio (presence)" },
  { to: "/agents/im", label: "IM (auto-reply)" },
  { to: "/agents/mcp", label: "MCP tools" },
  { to: "/agents/skill", label: "Skill install" },
  { to: "/auth", label: "Auth model", group: "Reference" },
  { to: "/hubs", label: "Hubs & URLs" },
];

export function DocsLayout() {
  return (
    <div className="min-h-screen bg-og-ink text-zinc-100">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-og-ink/90 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-4 px-4">
          <Link to="/" className="flex items-center gap-2.5">
            <img
              src="/og-logo.png"
              alt=""
              className="h-8 w-8 rounded-xl shadow-md shadow-orange-500/25"
              width={32}
              height={32}
            />
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-tight text-white">
                OpenGateway
              </div>
              <div className="text-[10px] font-medium uppercase tracking-wider text-orange-400/90">
                Docs · Self-host
              </div>
            </div>
          </Link>
          <nav className="flex items-center gap-3 text-xs text-zinc-400">
            <a
              href={MARKETING_ORIGIN}
              className="hidden hover:text-white sm:inline"
              rel="noreferrer"
            >
              Home
            </a>
            <a
              href={RAILWAY_DEPLOY}
              className="hidden hover:text-white md:inline"
              target="_blank"
              rel="noreferrer"
            >
              Railway
            </a>
            <a
              href={GITHUB_OSS}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg bg-gradient-to-b from-orange-400 to-orange-600 px-3 py-1.5 font-semibold text-orange-950 shadow-sm hover:from-orange-300 hover:to-orange-500"
            >
              Get started on GitHub
            </a>
          </nav>
        </div>
      </header>

      <div className="mx-auto flex max-w-6xl gap-8 px-4 py-8">
        <aside className="hidden w-56 shrink-0 md:block">
          <nav className="sticky top-20 space-y-0.5 text-sm">
            {NAV.map((item) => (
              <div key={item.to}>
                {item.group ? (
                  <div className="mb-1 mt-5 px-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 first:mt-0">
                    {item.group}
                  </div>
                ) : null}
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    cn(
                      "block rounded-lg px-2 py-1.5 text-[13px] transition",
                      isActive
                        ? "bg-orange-500/15 font-medium text-orange-300"
                        : "text-zinc-400 hover:bg-white/5 hover:text-zinc-200"
                    )
                  }
                >
                  {item.label}
                </NavLink>
              </div>
            ))}
          </nav>
        </aside>

        <main className="min-w-0 flex-1 pb-16">
          <div className="mb-6 flex flex-wrap gap-2 md:hidden">
            {[
              { to: "/oss", label: "Self-host" },
              { to: "/railway", label: "Railway" },
              { to: "/agents/connect", label: "Connect" },
            ].map((p) => (
              <NavLink
                key={p.to}
                to={p.to}
                className={({ isActive }) =>
                  cn(
                    "rounded-full border px-3 py-1 text-[11px] font-medium",
                    isActive
                      ? "border-orange-500/50 bg-orange-500/15 text-orange-200"
                      : "border-white/10 text-zinc-400"
                  )
                }
              >
                {p.label}
              </NavLink>
            ))}
          </div>
          <article className="docs-prose max-w-3xl">
            <Outlet />
          </article>
        </main>
      </div>

      <footer className="border-t border-white/10 py-8 text-center text-[11px] text-zinc-500">
        <div className="mx-auto max-w-6xl space-y-2 px-4">
          <p>
            <strong className="text-zinc-400">Self-host only</strong> · Apache-2.0
            ·{" "}
            <a
              href={GITHUB_OSS}
              className="text-orange-400/90 hover:text-orange-300"
              target="_blank"
              rel="noreferrer"
            >
              github.com/mrdulasolutions/open-gateway
            </a>
          </p>
          <p>
            One-click:{" "}
            <a
              href={RAILWAY_DEPLOY}
              className="text-orange-400/90 hover:text-orange-300"
              target="_blank"
              rel="noreferrer"
            >
              Deploy on Railway
            </a>
          </p>
          <p className="text-zinc-600">
            Agents need a hub URL + device key + the public{" "}
            <code className="text-zinc-500">opengateways</code> package.
          </p>
        </div>
      </footer>
    </div>
  );
}
